import React from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { Helmet } from "react-helmet-async";
import { Box, Typography } from "@mui/material";
import SignInComponent from "../../components/auth/SignIn";
import { clientConfig } from "../../config";
import DefaultLogoImg from "../../vendor/hyper_radius_logo.png";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const floatOrb1 = keyframes`
  0% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(50px, -50px) scale(1.1); }
  66% { transform: translate(-30px, 30px) scale(0.9); }
  100% { transform: translate(0, 0) scale(1); }
`;

const floatOrb2 = keyframes`
  0% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(-50px, 50px) scale(1.1); }
  66% { transform: translate(30px, -30px) scale(0.9); }
  100% { transform: translate(0, 0) scale(1); }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(135deg, #f0f4ff 0%, #f3ebff 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -100px;
    left: -100px;
    width: 50vw;
    height: 50vw;
    max-width: 600px;
    max-height: 600px;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%);
    border-radius: 50%;
    z-index: 0;
    pointer-events: none;
    animation: ${floatOrb1} 20s ease-in-out infinite;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -100px;
    right: -100px;
    width: 50vw;
    height: 50vw;
    max-width: 600px;
    max-height: 600px;
    background: radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%);
    border-radius: 50%;
    z-index: 0;
    pointer-events: none;
    animation: ${floatOrb2} 25s ease-in-out infinite reverse;
  }
`;

const ContentWrapper = styled(Box)`
  width: 100%;
  max-width: 450px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0px 10px 40px rgba(59, 130, 246, 0.08);
  border-radius: 24px;
  padding: 48px 40px;
  text-align: center;
  animation: ${fadeInUp} 0.8s ease-out forwards;
  position: relative;
  z-index: 1;

  @media (max-width: 600px) {
    padding: 32px 24px;
    border-radius: 16px;
  }
`;

const WelcomeText = styled.h2`
  color: #1e293b;
  font-size: 1.75rem;
  font-weight: 700;
  margin-top: 0;
  margin-bottom: 24px;
  opacity: 0;
  animation: ${fadeInUp} 0.8s ease-out forwards 0.3s;
`;

const LogoWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
  width: 100%;
`;

const Logo = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border: 4px solid #ffffff;
  position: relative;
  z-index: 1;
  animation: ${float} 6s ease-in-out infinite;
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;

  &:hover {
    transform: scale(1.05) translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
  }
`;

function SignIn() {
  const logoSrc = clientConfig.logoUrl || DefaultLogoImg;

  return (
    <PageWrapper>
      <Helmet title="Sign In" />

      <ContentWrapper>
        <LogoWrapper>
          <Logo src={logoSrc} alt={clientConfig.logoAlt} />
        </LogoWrapper>
        <WelcomeText>Welcome Back</WelcomeText>
        <SignInComponent isNewDesign />
      </ContentWrapper>
    </PageWrapper>
  );
}

export default SignIn;
